/**
 * The capacity area's query hooks (platform design §5.1.1; CA-3's routes, not yet landed).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import {
  capacityRequestSchema,
  type Capacity,
  type CapacityRequest,
  type CapacityRequestInput,
  type Consumption,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

export const CAPACITY_QUERY_KEY = (tenantId: string) => ['tenant', tenantId, 'capacity'] as const;
export const CAPACITY_REQUESTS_QUERY_KEY = (tenantId: string) =>
  ['tenant', tenantId, 'capacity-requests'] as const;
export const CONSUMPTION_QUERY_KEY = (tenantId: string) =>
  ['tenant', tenantId, 'consumption'] as const;

export function useCapacity(tenantId: string): UseQueryResult<Capacity, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Capacity, ApiFailureError>({
    queryKey: CAPACITY_QUERY_KEY(tenantId),
    queryFn: () =>
      client.getParsed(routePath(GET_ROUTES.capacity, { tenantId }), GET_ROUTES.capacity.schema),
  });
}

/** Figma `185:5432`'s consumption chart and per-campaign breakdown, both served by the one route
 *  (`api`'s `read_consumption`). */
export function useConsumption(tenantId: string): UseQueryResult<Consumption, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Consumption, ApiFailureError>({
    queryKey: CONSUMPTION_QUERY_KEY(tenantId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.creditsConsumption, { tenantId }),
        GET_ROUTES.creditsConsumption.schema,
      ),
  });
}

export function useCapacityRequests(
  tenantId: string,
): UseQueryResult<CapacityRequest[], ApiFailureError> {
  const client = useApiClient();

  return useQuery<CapacityRequest[], ApiFailureError>({
    queryKey: CAPACITY_REQUESTS_QUERY_KEY(tenantId),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.creditRequests, { tenantId }),
        GET_ROUTES.creditRequests.schema,
      );
      return items;
    },
  });
}

/** The API holds at most one of these per tenant per kind and refuses a second with a 409. */
export const OPEN_STATES: ReadonlySet<CapacityRequest['state']> = new Set(['OPEN', 'IN_REVIEW']);

export function openRequestOf(
  requests: readonly CapacityRequest[],
  kind: CapacityRequest['kind'],
): CapacityRequest | undefined {
  return requests.find((request) => request.kind === kind && OPEN_STATES.has(request.state));
}

/** `undefined` until the request list has loaded, so a caller never offers a request action on
 *  a guess. */
export function useHasOpenRequest(
  tenantId: string,
  kind: CapacityRequest['kind'],
): boolean | undefined {
  const { data } = useCapacityRequests(tenantId);
  return data === undefined ? undefined : openRequestOf(data, kind) !== undefined;
}

/** §18.6: a request only ever moves to `OPEN`. Invalidates the request list so a caller sees its
 *  own submission; the balance/slot count itself changes only once an operator decides it
 *  (CA-7), so nothing here invalidates `CAPACITY_QUERY_KEY`. */
export function useCreateCapacityRequest(
  tenantId: string,
): UseMutationResult<CapacityRequest, ApiFailureError, CapacityRequestInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<CapacityRequest, ApiFailureError, CapacityRequestInput>({
    mutationFn: (body) =>
      client.postParsed(`/tenants/${tenantId}/credit-requests`, body, capacityRequestSchema),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CAPACITY_REQUESTS_QUERY_KEY(tenantId) }),
  });
}

export function useWithdrawCapacityRequest(
  tenantId: string,
): UseMutationResult<void, ApiFailureError, { requestId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, { requestId: string }>({
    mutationFn: ({ requestId }) => client.del(`/tenants/${tenantId}/credit-requests/${requestId}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CAPACITY_REQUESTS_QUERY_KEY(tenantId) }),
  });
}
